import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import SimpleDropdown from '~/shared/components/SimpleDropdown';
import Typography from '~/shared/components/Typography';

import {
  formatCapacityValue,
  tibToUnit,
  unitToTib,
  type CapacityBounds,
  type CapacityUnit,
} from './utils/capacityFormat';

const CAPACITY_UNITS: CapacityUnit[] = ['TiB', 'GiB'];

// Must match HANDLE_CLASS `size-6` (1.5rem @ 16px root).
const HANDLE_SIZE_PX = 24;
const HANDLE_HALF_PX = HANDLE_SIZE_PX / 2;
const HANDLE_TRAVEL_CSS = `(100% - ${HANDLE_SIZE_PX}px)`;

function handleLeftStyle(pct: number): { left: string } {
  return { left: `calc(${HANDLE_HALF_PX}px + ${HANDLE_TRAVEL_CSS} * ${pct / 100})` };
}

function rangeFillStyle(loPct: number, hiPct: number): { left: string; width: string } {
  return {
    left: `calc(${HANDLE_HALF_PX}px + ${HANDLE_TRAVEL_CSS} * ${loPct / 100})`,
    width: `calc(${HANDLE_TRAVEL_CSS} * ${(hiPct - loPct) / 100})`,
  };
}

function pointerXToPct(clientX: number, sliderRect: DOMRect): number {
  const travelWidth = sliderRect.width - HANDLE_SIZE_PX;
  if (travelWidth <= 0) return 0;
  return Math.max(0, Math.min(1, (clientX - sliderRect.left - HANDLE_HALF_PX) / travelWidth));
}

const INPUT_GROUP_CLASS =
  'flex flex-1 items-center gap-1 h-11 rounded-lg border border-gray-300 bg-white py-2.5 pl-4 pr-3';
const NUMBER_INPUT_CLASS =
  'w-[60px] min-w-0 border-0 bg-transparent p-0 text-sm font-medium text-primary-900 outline-none';
const HANDLE_CLASS =
  'absolute top-0 z-10 size-6 -translate-x-1/2 cursor-pointer touch-none rounded-full border-2 border-primary-500 bg-white p-0 shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

function CapacityUnitDropdown({
  value,
  onChange,
  'aria-label': ariaLabel,
}: {
  value: CapacityUnit;
  onChange: (unit: CapacityUnit) => void;
  'aria-label': string;
}) {
  return (
    <SimpleDropdown<CapacityUnit>
      items={CAPACITY_UNITS}
      selectedItem={value}
      onSelect={onChange}
      aria-label={ariaLabel}
      portal
      align="top-left"
      className="shrink-0"
      menuButtonClassName="!h-auto !w-auto !min-w-0 !border-0 !bg-transparent !px-0.5 !py-0 !shadow-none hover:!border-0 active:!border-0 focus:!border-0"
      menuButtonLabelClassName="text-sm font-medium text-gray-600"
      menuItemsClassName="!w-[92px] !min-w-[92px]"
      menuItemClassName="!h-9 !px-3 !py-2"
      renderButtonEnd={() => <div className="w-0 -mx-0.5" />} //for the gap-2
    />
  );
}

function getSliderAriaProps(
  tib: number,
  unit: CapacityUnit,
  minTib: number,
  maxTib: number
): {
  'aria-valuemin': number;
  'aria-valuemax': number;
  'aria-valuenow': number;
  'aria-valuetext': string;
} {
  return {
    'aria-valuemin': tibToUnit(minTib, unit),
    'aria-valuemax': tibToUnit(maxTib, unit),
    'aria-valuenow': tibToUnit(tib, unit),
    'aria-valuetext': `${formatCapacityValue(tib, unit)} ${unit}`,
  };
}

function getKeyboardStepTib(span: number, unit: CapacityUnit): number {
  return Math.max(span / 20, unit === 'GiB' ? 1 / 1024 : 0.01);
}

export default function CapacityRangeFilter({
  bounds,
  pendingCapacityMin,
  pendingCapacityMax,
  setPendingCapacityMin,
  setPendingCapacityMax,
  autoFocusLo = false,
}: {
  bounds: CapacityBounds;
  pendingCapacityMin: number;
  pendingCapacityMax: number;
  setPendingCapacityMin: (min: number) => void;
  setPendingCapacityMax: (max: number) => void;
  autoFocusLo?: boolean;
}) {
  const [loUnit, setLoUnit] = useState<CapacityUnit>('TiB');
  const [hiUnit, setHiUnit] = useState<CapacityUnit>('TiB');
  const [dragging, setDragging] = useState<'lo' | 'hi' | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const loHandleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!autoFocusLo) return;
    loHandleRef.current?.focus();
  }, [autoFocusLo]);

  if (bounds.max <= bounds.min) {
    return (
      <Typography variant="p2-semi" className="text-gray-600">
        No capacity range available
      </Typography>
    );
  }

  const span = Math.max(1, bounds.max - bounds.min);
  const toPct = (v: number) => ((v - bounds.min) / span) * 100;
  const lp = toPct(pendingCapacityMin);
  const hp = toPct(pendingCapacityMax);

  const clampLo = (tib: number) => Math.max(bounds.min, Math.min(pendingCapacityMax, tib));
  const clampHi = (tib: number) => Math.max(pendingCapacityMin, Math.min(bounds.max, tib));

  const onSliderPointer = (clientX: number, which: 'lo' | 'hi') => {
    const el = sliderRef.current;
    if (!el) return;
    const pct = pointerXToPct(clientX, el.getBoundingClientRect());
    const tib = bounds.min + pct * span;
    if (which === 'lo') {
      setPendingCapacityMin(clampLo(tib));
    } else {
      setPendingCapacityMax(clampHi(tib));
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDragging(null);
  };

  const onHandlePointerDown = (which: 'lo' | 'hi') => (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.focus();
    setDragging(which);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onHandlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging) return;
    onSliderPointer(e.clientX, dragging);
  };

  const onHandleKeyDown = (which: 'lo' | 'hi') => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      return;
    }

    const unit = which === 'lo' ? loUnit : hiUnit;
    const stepTib = getKeyboardStepTib(span, unit);
    const current = which === 'lo' ? pendingCapacityMin : pendingCapacityMax;

    let next: number | null = null;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = current - stepTib;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = current + stepTib;
        break;
      case 'Home':
        next = which === 'lo' ? bounds.min : pendingCapacityMin;
        break;
      case 'End':
        next = which === 'lo' ? pendingCapacityMax : bounds.max;
        break;
      default:
        return;
    }

    e.preventDefault();
    if (which === 'lo') {
      setPendingCapacityMin(clampLo(next));
    } else {
      setPendingCapacityMax(clampHi(next));
    }
  };

  const loAria = getSliderAriaProps(
    pendingCapacityMin,
    loUnit,
    bounds.min,
    pendingCapacityMax
  );
  const hiAria = getSliderAriaProps(
    pendingCapacityMax,
    hiUnit,
    pendingCapacityMin,
    bounds.max
  );

  return (
    <div className="flex flex-col gap-5 py-2 pb-4">
      <div className="flex items-center gap-3">
        <div className={INPUT_GROUP_CLASS}>
          <input
            type="number"
            className={NUMBER_INPUT_CLASS}
            step="any"
            aria-label="Minimum capacity"
            value={formatCapacityValue(pendingCapacityMin, loUnit)}
            onChange={(e) => {
              let v = parseFloat(e.target.value);
              if (!Number.isFinite(v)) v = tibToUnit(bounds.min, loUnit);
              setPendingCapacityMin(clampLo(unitToTib(v, loUnit)));
            }}
          />
          <CapacityUnitDropdown
            value={loUnit}
            onChange={setLoUnit}
            aria-label="Minimum capacity unit"
          />
        </div>
        <div className="h-0 w-2.5 shrink-0 border-t-2 border-gray-400" />
        <div className={INPUT_GROUP_CLASS}>
          <input
            type="number"
            className={NUMBER_INPUT_CLASS}
            step="any"
            aria-label="Maximum capacity"
            value={formatCapacityValue(pendingCapacityMax, hiUnit)}
            onChange={(e) => {
              let v = parseFloat(e.target.value);
              if (!Number.isFinite(v)) v = tibToUnit(bounds.max, hiUnit);
              setPendingCapacityMax(clampHi(unitToTib(v, hiUnit)));
            }}
          />
          <CapacityUnitDropdown
            value={hiUnit}
            onChange={setHiUnit}
            aria-label="Maximum capacity unit"
          />
        </div>
      </div>
      <div className="relative h-6 w-full" ref={sliderRef}>
        <div className="absolute top-2 inset-x-0 h-2">
          <div className="absolute inset-0 rounded bg-primary-70" />
          <div
            className="absolute top-0 h-full rounded bg-primary-500"
            style={rangeFillStyle(lp, hp)}
          />
        </div>
        <button
          ref={loHandleRef}
          type="button"
          role="slider"
          aria-orientation="horizontal"
          aria-label="Minimum capacity"
          className={clsx(HANDLE_CLASS, dragging === 'lo' && 'bg-bg')}
          style={handleLeftStyle(lp)}
          onPointerDown={onHandlePointerDown('lo')}
          onPointerMove={onHandlePointerMove}
          onPointerUp={endDrag}
          onLostPointerCapture={endDrag}
          onKeyDown={onHandleKeyDown('lo')}
          {...loAria}
        />
        <button
          type="button"
          role="slider"
          aria-orientation="horizontal"
          aria-label="Maximum capacity"
          className={clsx(HANDLE_CLASS, dragging === 'hi' && 'bg-bg')}
          style={handleLeftStyle(hp)}
          onPointerDown={onHandlePointerDown('hi')}
          onPointerMove={onHandlePointerMove}
          onPointerUp={endDrag}
          onLostPointerCapture={endDrag}
          onKeyDown={onHandleKeyDown('hi')}
          {...hiAria}
        />
      </div>
    </div>
  );
}
